import { useFetchLatestAppoinmentResults } from '@/api/hooks/useFetchLatestAppointmentResults'
import { ResultConditionsList } from '@/components/AppointmentResultsPage/ResultsConditionsList'
import { WallyHealthScore } from '@/components/AppointmentResultsPage/WallyHealthScore'
import { Glyph } from '@/components/svgs/Glyph'
import { colors } from '@/constants/colors'
import { Header } from '@/ui/core/header'
import { Swivel } from '@/ui/core/motion'
import { Pressable } from '@/ui/core/pressable'
import { HStack, VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { cn } from '@/ui/utils'
import { useState } from 'react'
import { ScrollView, View } from 'react-native'
import Animated, { FadeIn, LinearTransition, ReduceMotion } from 'react-native-reanimated'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

const GROW = LinearTransition.duration(320).reduceMotion(ReduceMotion.System)

/**
 * The report as one card that grows. Closed it is the score and a line saying how many findings
 * came with it; opened it takes the whole page — the footprint the care team thread already uses,
 * inset from both edges, under the header with its own rounded top, down to the home indicator.
 *
 * The score stays exactly where it is through the change: the card grows beneath it, the rule and
 * the rows arriving underneath, so the number never moves. The chevron is the whole affordance:
 * right to open, as every row in the app points, and up to fold away.
 */
const ViewableAppointmentResults = () => {
	const { appointmentResults } = useFetchLatestAppoinmentResults()
	const insets = useSafeAreaInsets()
	const [open, setOpen] = useState(false)
	const conditions = appointmentResults?.conditions ?? []
	const count = conditions.length

	return (
		<Animated.View
			layout={GROW}
			className={cn(
				'mx-5 overflow-hidden rounded-note bg-blush lg:w-full lg:max-w-[780px] lg:self-center',
				open && 'flex-1'
			)}
			style={{ marginBottom: Math.max(insets.bottom, 12) }}
		>
			<VStack className="px-4 pt-4">
				<HStack className="items-center justify-between">
					<Header variant="h7" tone="muted">
						From your last visit
					</Header>
				</HStack>
				<WallyHealthScore score={appointmentResults?.healthScore} />
			</VStack>

			{count > 0 && (
				<>
					{/* The rule runs the card's full width, so the score reads as its head. */}
					<View className="h-px" style={{ backgroundColor: colors.divider }} />
					<Pressable
						onPress={() => setOpen((was) => !was)}
						accessibilityRole="button"
						accessibilityState={{ expanded: open }}
						accessibilityLabel={
							open
								? 'Hide what we found'
								: `What we found, ${count} ${count === 1 ? 'finding' : 'findings'}`
						}
						className="min-h-11 flex-row items-center justify-between px-4 py-3"
					>
						<Text size="body1" weight="bold">
							{open
								? 'What we found'
								: `${count} ${count === 1 ? 'finding' : 'findings'}`}
						</Text>
						<Swivel deg={open ? -90 : 0}>
							<Glyph name="chevron" size={15} color={colors.ink} />
						</Swivel>
					</Pressable>
					{open ? (
						<Animated.View entering={FadeIn.duration(220)} className="flex-1">
							<ScrollView
								className="flex-1"
								showsVerticalScrollIndicator={false}
								contentContainerClassName="px-4 pb-2"
							>
								<ResultConditionsList conditions={conditions} />
							</ScrollView>
						</Animated.View>
					) : null}
				</>
			)}
		</Animated.View>
	)
}

export { ViewableAppointmentResults }
